import { addDays, isBefore, startOfDay } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';

import { usePriceHistoryList } from '~/react-query-hooks/prices/usePriceHistoryList';
import { Tracking } from '~/services/tracking';

export const NEW_YORK_TIMEZONE = 'America/New_York';

interface UsePriceChangeMarketReactionDataProps {
  fromDateEST?: Date;
  toDateEST?: Date;
  tags: string[];
  tickers: string[];
  enabled?: boolean;
}

export const usePriceChangeMarketReactionData = ({
  fromDateEST,
  toDateEST,
  tags,
  tickers,
  enabled = true,
}: UsePriceChangeMarketReactionDataProps) => {
  const fromDateUTC = fromDateEST
    ? fromZonedTime(fromDateEST, NEW_YORK_TIMEZONE)
    : undefined;
  const toDateUTC = toDateEST
    ? fromZonedTime(toDateEST, NEW_YORK_TIMEZONE)
    : undefined;

  const { data: priceDataList, isLoading: isPriceHistoryLoading } =
    usePriceHistoryList({
      tags,
      tickers,
      resolution: '1m',
      dateFrom: fromDateUTC?.toISOString() ?? '',
      dateTo: toDateUTC?.toISOString() ?? '',
      enabled: enabled && !!fromDateUTC && !!toDateUTC,
    });

  const { data: previousPriceDataList, isLoading: isPrevCloseLoading } =
    usePriceHistoryList({
      tags,
      tickers,
      resolution: '1D',
      dateFrom: fromDateUTC
        ? addDays(startOfDay(fromDateUTC), -4).toISOString()
        : '',
      dateTo: toDateUTC ? addDays(toDateUTC, 4).toISOString() : '',
      enabled: enabled && !!fromDateUTC && !!toDateUTC,
    });

  if (isPriceHistoryLoading || isPrevCloseLoading) {
    return {
      data: [],
      isLoading: true,
    };
  }

  const data = priceDataList.map((priceData, index) => {
    const prevClosePrices = previousPriceDataList.find(
      p => p.entityTag === priceData.entityTag
    )?.data;

    if (!prevClosePrices) {
      return {
        data: [],
        lastProportionChange: undefined,
        prevCloseDate: undefined,
      };
    }

    if (!priceData.data?.length) {
      return {
        data: [],
        lastProportionChange: undefined,
        prevCloseDate: undefined,
      };
    }

    const firstDayOfTheWindow = priceData.data[0].time;
    const data = priceData.data.map(price => {
      const prevCloseData = prevClosePrices.findLast(prevClosePrice =>
        isBefore(prevClosePrice.time, startOfDay(price.time))
      );

      if (!prevCloseData) {
        Tracking.captureException(
          `usePriceChangeMarketReactionData - tag: ${tags[index]} - prevCloseData not found`
        );
      }

      const prevClose = prevCloseData?.close ?? 0;
      const value = prevClose
        ? ((price.close - prevClose) / prevClose) * 100
        : undefined;

      return {
        time: price.time,
        value,
      };
    });

    const startOfDayUTC = firstDayOfTheWindow.replace(/T.*/, 'T00:00:00Z');
    const prevCloseData = prevClosePrices.findLast(
      prevClosePrice => prevClosePrice.time < startOfDayUTC
    );
    const lastKnownPrice = priceData.data[priceData.data.length - 1]?.close;

    const lastProportionChange =
      prevCloseData?.close && lastKnownPrice
        ? ((lastKnownPrice - prevCloseData.close) / prevCloseData.close) * 100
        : undefined;

    return {
      data,
      lastProportionChange,
      prevCloseData,
    };
  });

  return {
    data,
    isLoading: false,
  };
};
